import { FC } from 'react';
import { Box, Stack, Button, Link, ListItem } from '@mui/material';
import { NavLink } from 'react-router-dom';
import { AccountPhoto } from '@barracuda/shared/components/AccountPhoto/AccountPhoto';
import {
  hamburgerMenuStyle,
  logoLink,
  logOutStyle,
  navbar,
  navbarDesktop,
  navbarList,
  navbarListLoggedIn,
  navbarLoggedInListSpacer,
  navbarLoggedInActionItem,
  navbarNavLoggedIn,
  signInButtonNavbar,
  signInUpButtonsContainer,
  signUpButtonNavbar,
  navbarLogin
} from './HeaderNavbar.style';
import Logo from '@/assets/images/pearson-logo-odyssey.svg';
import { useLocation } from 'react-router-dom';
import { Auth } from '@barracuda/shared';
import { theme } from '@barracuda/shared/styles/theme';
import { useAppContext } from '@/context/App.context';
import HamburgerMenu from '@/assets/images/hamburger-menu.svg?react';
import { useLocalizedNavigate } from '@/hooks/useLocalizedNavigate';
import { useTranslation } from 'react-i18next';
import Img from '@barracuda/shared/components/Primitive/Img';
import { mixpanelApi } from '@barracuda/shared/analytics/mixpanel';
import useGenerateLinks from './useGenerateLinks';
import { renderDesktopItems } from '../NavbarMenuItem/navbarMenu.utils';
import { useSetIsMenuOpen } from '@/store/useAppStore';
import { LocalizedNavLink } from '../LocalizedNavLink/LocalizedNavLink';

export const HeaderNavbar: FC<{ code?: string }> = () => {
  const { isLoggedIn, user, setUser, setIsLoggedIn, language } = useAppContext();
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const navigate = useLocalizedNavigate();
  const setIsMenuOpen = useSetIsMenuOpen();

  const logout = () => {
    mixpanelApi.onLogout();

    Auth.logout(user?.profile?.loggedBefore);
    setUser(undefined);
    setIsLoggedIn(false);
    navigate('/login');
  };

  const { links } = useGenerateLinks({ isLoggedIn, user });

  const handleSignUp = () => {
    navigate('/join');
  };

  const handleSignIn = () => {
    navigate('/login');
  };

  const isV2Page = ['/login', '/join', '/reset-password'].some((path) => pathname.includes(path));

  return (
    <Box component='header' sx={[navbar, isV2Page && navbarLogin]}>
      <Box sx={navbarDesktop}>
        <Link
          component={NavLink}
          to={`/${language}${isLoggedIn ? '/dashboard/tests' : '/'}`}
          sx={logoLink}
        >
          <Img src={Logo} alt={process.env.REACT_APP_GENERAL_PRODUCT_NAME} id='pte-express-logo' />
        </Link>
        <Box
          component='nav'
          aria-label={t('menuLabels.primary')}
          sx={isLoggedIn ? navbarNavLoggedIn : {}}
        >
          <Box component='ul' sx={isLoggedIn ? navbarListLoggedIn : navbarList}>
            {isLoggedIn ? (
              <>
                {links}
                <ListItem aria-hidden disablePadding sx={navbarLoggedInListSpacer} />
                <ListItem disablePadding sx={navbarLoggedInActionItem}>
                  <LocalizedNavLink
                    to='/dashboard/account'
                    style={{ textDecoration: 'none', display: 'flex' }}
                    aria-label={t('menuLabels.account')}
                  >
                    <AccountPhoto imageUri={user?.imageUri} width='42px' height='42px' />
                  </LocalizedNavLink>
                </ListItem>
                <ListItem disablePadding sx={navbarLoggedInActionItem}>
                  <Button type='button' variant='text' onClick={logout} sx={logOutStyle}>
                    {t('menuLabels.logOut')}
                  </Button>
                </ListItem>
              </>
            ) : (
              renderDesktopItems()
            )}
          </Box>
        </Box>
        {isLoggedIn && (
          <Button
            sx={{ ...hamburgerMenuStyle, flexShrink: 0 }}
            onClick={() => setIsMenuOpen?.(true)}
            aria-label={t('menuLabels.openMenu')}
          >
            <HamburgerMenu fill={theme.palette.common.darkGrey2} aria-hidden />
          </Button>
        )}
      </Box>
      {!isLoggedIn && (
        <Stack sx={signInUpButtonsContainer}>
          <Button size='large' sx={signUpButtonNavbar} onClick={handleSignUp} id='header-sign-up'>
            <Box display='flex' gap='8px' alignItems='center'>
              <Box>{t('signUpText')}</Box>
            </Box>
          </Button>
          <Button size='large' sx={signInButtonNavbar} onClick={handleSignIn} id='header-sign-in'>
            {t('loginInText')}
          </Button>
        </Stack>
      )}
      {!isLoggedIn && !pathname.includes('verification') && (
        <Button
          sx={hamburgerMenuStyle}
          onClick={() => setIsMenuOpen?.(true)}
          aria-label={t('menuLabels.openMenu')}
        >
          <HamburgerMenu fill={theme.palette.common.white} aria-hidden />
        </Button>
      )}
    </Box>
  );
};
